import React from 'react';
import { Route, RouteProps, Switch } from 'react-router';
import AreaLibraryPage from '../containers/AreaLibraryPage';
import Order from '../containers/Order';
import RealtimeOrderProduct from '../containers/RealtimeOrderProduct';
import Dashboard from '../containers/Dashboard';
import RealtimeOrderProductNew from "../containers/RealtimeOrderProductNew";
import RealtimeProductNew from "../containers/RealtimeProductNew";
import WebOrderNew from "../containers/WebOrderNew";
import ArchiveOrderNew from '../containers/ArchiveOrderNew';
import Orders from "../containers/Orders";
import Basket from "../containers/Basket";
import { useSelector } from "react-redux";
import { selectUser } from "../store/selectors/UserSelectors";
import BasketPolicy from "../policies/BasketPolicy";
import OrderDisseminationRequest from "../containers/OrderDisseminationRequest";
import PendingOrders from "../containers/PendingOrders";
import CcpOrders from "../containers/CcpOrders";
import OrderPolicy from "../policies/OrderPolicy";
import AreaLibraryPolicy from '../policies/AreaLibraryPolicy';
import PublicOrders from '../containers/PublicOrders';
import EuCutOutOrderNew from '../containers/EuCutOutOrderNew';
import EuCutOutOrderEdit from "../containers/EuCutOutOrderEdit";
import { User, UserType } from "../entities/User";
import NotAuthenticatedScreen from "../components/NotAuthenticatedScreen";

type ResourcePathId = number | string;

export const paths = {
  root: '/',
  basket: '/basket',
  areas: '/areas',
  orders: '/orders',
  pendingOrders: '/pending-orders',
  order: (id: ResourcePathId) => `/orders/${id}`,
  realtimeOrderProduct: (orderId: ResourcePathId, productId: ResourcePathId) => `/orders/${orderId}/products/${productId}`,
  realtimeOrderProductNew: (orderId: ResourcePathId) => `/orders/${orderId}/products/new`,
  euCutOutOrderEdit: (orderId: ResourcePathId) => `/orders/${orderId}/edit`,
  ordersByFolder: (folderId: ResourcePathId) => `/orders/folders/${folderId}`,
  orderDisseminationRequest: (orderId: ResourcePathId) => `/orders/${orderId}/diss`,
  realtimeOrderNew: '/orders/new',
  webOrderNew: '/web-orders/new',
  archiveOrderNew: '/archive-orders/new',
  ccpOrders: '/ccp-orders',
  publicOrders: '/public-orders'
};

const ProtectedRoute = (props: { user: User } & RouteProps) => {
  const { user, ...routerProps } = props;
  const isUserLoggedIn = user.type !== UserType.Unknown;

  return (
    <Route
      {...routerProps}
      component={isUserLoggedIn ? routerProps.component : NotAuthenticatedScreen}
    />
  )
}

const Router = () => {
  const user = useSelector(selectUser);

  return (
    <Switch>
      <Route path={paths.root} exact component={Dashboard} />
      {
        BasketPolicy.canAccess(user) &&
        <Route path={paths.basket} exact component={Basket} />
      }
      <Route path={paths.webOrderNew} component={WebOrderNew} />
      <Route path={paths.archiveOrderNew} component={ArchiveOrderNew} />
      <Route path={paths.realtimeOrderNew} component={RealtimeProductNew} />

      <ProtectedRoute user={user} path={paths.order(':id')} exact component={Order} />
      <ProtectedRoute user={user} path={paths.realtimeOrderProductNew(':orderId')}
                      component={RealtimeOrderProductNew} />
      <ProtectedRoute user={user} path={paths.realtimeOrderProduct(':orderId', ':productId')} exact
                      component={RealtimeOrderProduct} />
      <ProtectedRoute user={user} path={paths.euCutOutOrderEdit(':orderId')} exact component={EuCutOutOrderEdit} />

      {
        <ProtectedRoute user={user} path={paths.orderDisseminationRequest(':orderId')} exact
                        component={OrderDisseminationRequest} />
      }
      <ProtectedRoute user={user} path={paths.orders} component={Orders} />

      {
        OrderPolicy.canViewPendingOrders(user) &&
        <ProtectedRoute user={user} path={paths.pendingOrders} component={PendingOrders} />
      }

      {
        AreaLibraryPolicy.canAccess(user) &&
        <ProtectedRoute user={user} path={paths.areas} exact component={AreaLibraryPage} />
      }

      {
        OrderPolicy.canViewCcpOrders(user) &&
        <ProtectedRoute user={user} path={paths.ccpOrders} exact component={CcpOrders} />
      }

      {
        OrderPolicy.canViewPublicOrders(user) &&
        <ProtectedRoute user={user} path={paths.publicOrders} exact component={PublicOrders} />
      }
    </Switch>
  );
};

export default Router;
